import React, { useRef, useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Send, Sparkles } from 'lucide-react';
import { useAzureAIChat } from '@/contexts/AzureAIChatContext';
import { Message } from '@/components/vapi/Message';
import { TypingIndicator } from '@/components/vapi/TypingIndicator';

export const AzureChatInterface = () => {
  const { messages, sendMessage, isAssistantTyping } = useAzureAIChat();
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, isAssistantTyping]);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;

    setIsSending(true);
    await sendMessage(inputValue);
    setInputValue('');
    setIsSending(false);

    // Refocus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedPrompts = [
    'What services does Digian offer?',
    'Tell me about your AI solutions',
    'How can Digian help my business?',
    'How can i contact digian AI?'
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center space-y-4 max-w-sm px-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">
                  Hi! I'm Digian AI Assistant
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  I'm here to help you learn about Digian's data analytics solutions, AI services, and how we can help transform your business with intelligent automation.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-medium">Try asking:</p>
                {suggestedPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => setInputValue(prompt)}
                    className="block w-full text-left px-3 py-2 text-sm text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((message, index) => (
              <Message
                key={index}
                message={message}
                isUser={message.role === 'user'}
              />
            ))}
            {isAssistantTyping && <TypingIndicator />}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="border-t border-slate-200 p-4 bg-white">
        <div className="flex space-x-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything..."
            className="flex-1"
            disabled={isSending}
            maxLength={500}
          />
          <Button
            onClick={handleSend}
            size="icon"
            disabled={!inputValue.trim() || isSending}
            className="bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {inputValue.length > 450 && (
          <p className="text-xs text-slate-400 mt-1">
            {500 - inputValue.length} characters remaining
          </p>
        )}
      </div>
    </div>
  );
};
